import React, { useContext, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "@reach/window-size";
import { formatValue } from "../../utils";
import CustomTable from "../../../../components/common/CustomTable";
import Tools from "../Tools";
import { StyledTransactionsTable, MobileBody } from "./style";
import { useAppSelector } from "../../../../app/hooks";
import { formatCurrencyAmount } from "../../../../utils/currency";
import dayjs from "dayjs";
import Loader from "../../../../components/common/Loader";
import {
  EmptyWrapper,
  EmptyWrapperTitle,
  EmptyWrapperSubTitle,
} from "../../../Balances/style";
import EmptyMobileCard from "../../../../components/emptyStates/EmptyMobileCard";
import WalletCard from "../WalletCard";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import CustomMobileTable from "../../../../components/common/CustomMobileTable";
import { WALLET_TRANSACTIONS_TABLE_COLUMNS } from "../../const";
import {getWalletTransactions} from "../../../../app/queries/wallets";
import {walletTransactionsProps} from "../../../../types/walletTypes";
import { AxiosError } from "axios";

const EmptyBalanceDescription = () => {
  return (
    <EmptyWrapper>
      <EmptyWrapperTitle>No transactions yet</EmptyWrapperTitle>
      <EmptyWrapperSubTitle>
        All received or sent transactions will appear here
      </EmptyWrapperSubTitle>
    </EmptyWrapper>
  );
};

const TransactionsTable = () => {
  const { width } = useWindowSize();
  const { businessId } = useAppSelector((state) => state.users);
  const [walletTransactionState, setWalletTransactionState] =
    useState<walletTransactionsProps>();

  const payload = {
    businessId,
    tnxStatus: walletTransactionState?.status,
    pageNumber: walletTransactionState?.pageNumber || 1,
    pageSize: walletTransactionState?.pageSize || 10,
  };
  const { data: wallets, error, isLoading } = getWalletTransactions(payload);

  const { onShowAlert } = useContext(NetworkErrorAlertContext);

  const handleChange = (field: keyof walletTransactionsProps, value: string | number) => {
    setWalletTransactionState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError?.response?.data?.message || "An error occurred";
      onShowAlert("", message);
    }
  }, [error]);

  const onPageChange = (page: number, size: number) => {
    handleChange("pageNumber", page);
    handleChange("pageSize", size);
  };

  const TRANSACTION_LIST =
    wallets?.walletTransactions?.map(({ walletTransaction }) => ({
      key: walletTransaction.id,
      no: walletTransaction.id,
      transId: walletTransaction.walletTnxId || "N/A",
      type: formatValue(walletTransaction.userTransactionType),
      amount: walletTransaction.toWallet
        ? formatCurrencyAmount(
            walletTransaction.toWallet.currency,
            walletTransaction.amount,
          )
        : "N/A",
      status: walletTransaction.tnxStatus,
      date: walletTransaction.createdAt
        ? dayjs(walletTransaction.createdAt).format("DD.MM.YYYY ⎮ HH:mm")
        : "N/A",
    })) || [];

  const FILTERED_WALLET_TRANSACTIONS = useMemo(() => {
    return TRANSACTION_LIST.filter((item) =>
      Object.values(item).some(
        (val) =>
          typeof val === "string" &&
          val
            .toLowerCase()
            .includes(walletTransactionState?.search?.toLowerCase() || ""),
      ),
    );
  }, [TRANSACTION_LIST, walletTransactionState?.search]);


  return (
    <StyledTransactionsTable>
      <Loader isLoading={isLoading} />
      <h3>Transactions</h3>

      {TRANSACTION_LIST?.length > 0 ? (
        <Tools
          search={walletTransactionState?.search}
          setSearch={(data) => handleChange("search", data)}
          showButtons={false}
        />
      ) : null}

      {width > 767 ? (
        <CustomTable
          extraEmptyChildren={<EmptyBalanceDescription />}
          columns={WALLET_TRANSACTIONS_TABLE_COLUMNS}
          dataSource={FILTERED_WALLET_TRANSACTIONS}
          currentPage={walletTransactionState?.pageNumber}
          totalItemCount={wallets?.totalCount}
          onPageChange={onPageChange}
        />
      ) : (
        <MobileBody>
          <CustomMobileTable
            data={FILTERED_WALLET_TRANSACTIONS}
            WalletCard={WalletCard}
            EmptyMobileCard={EmptyMobileCard}
            currentPage={walletTransactionState?.pageNumber}
            totalItemCount={wallets?.totalCount}
            onPageChange={onPageChange}
            pageSize={walletTransactionState?.pageSize}
            pageSizeOptions={[10, 20, 50]}
          />
        </MobileBody>
      )}
    </StyledTransactionsTable>
  );
};

export default TransactionsTable;
